import { Link } from 'react-router-dom'
import { ButtonSpinner } from '../components/ButtonSpinner'
import { SavedAddressList } from '../components/SavedAddressList'
import { CheckoutDeliveryPlan } from '../components/CheckoutDeliveryPlan'
import { CheckoutDialog } from '../components/CheckoutDialog'
import { CheckoutPlacing } from '../components/CheckoutPlacing'
import { CheckoutStepper } from '../components/CheckoutStepper'
import { CheckoutSuccess } from '../components/CheckoutSuccess'
import { ExpressConfirmDialog } from '../components/ExpressConfirmDialog'
import { Icon } from '../components/Icon'
import { ADDRESS_UI } from '../constants/addresses'
import { PINCODE_LENGTH } from '../constants/location'
import { AUTH_PATHS } from '../constants/auth'
import { CART_UI } from '../constants/cart'
import { ICON_FALLBACK } from '../constants/icons'
import {
  CHECKOUT_OUTCOME_COPY,
  CHECKOUT_PAYMENT_OPTIONS,
  CHECKOUT_STEP,
  CHECKOUT_UI,
  ORDER_PATHS,
  type CheckoutHeaderProps,
} from '../constants/orders'
import { PAYMENT_METHOD } from '../constants/settings'
import { cartLineWasTotal } from '../helpers/cart'
import { addonsForCheckoutLine, checkoutLineMeta } from '../helpers/checkout'
import { checkoutPaymentOutcome } from '../helpers/checkout-payment'
import { cx, formatAddonPrice, formatCurrency } from '../helpers/format'
import { sanitizePincodeInput } from '../helpers/location'
import { checkoutPaymentChoices } from '../helpers/settings'
import { useAuth } from '../hooks/useAuth'
import { useCart } from '../hooks/useCart'
import { useCheckout } from '../hooks/useCheckout'
import '../styles/checkout-page.scss'

export function CheckoutPage() {
  const { isAuthenticated } = useAuth()
  const { lines, itemCount, subtotal, compareAtTotal, savings } = useCart()
  const {
    form,
    updateForm,
    setBillingSameAsShipping,
    submitting,
    placingPhase,
    error,
    step,
    addressComplete,
    placedOrders,
    city,
    locationLabel,
    shippingPincode,
    pincodeEditable,
    pincodeResolving,
    savedAddresses,
    selectedAddressId,
    selectSavedAddress,
    allowedPaymentMethod,
    couponInput,
    setCouponInput,
    couponError,
    applyCoupon,
    lineAddons,
    addLineAddon,
    canSplit,
    canSchedule,
    canScheduleExpress,
    expressItems,
    standardItems,
    deliveryEstimate,
    addressRecap,
    splitMode,
    setSplitMode,
    scheduleMode,
    setScheduleMode,
    scheduleDates,
    scheduledDate,
    setScheduledDate,
    scheduleSlots,
    scheduledSlot,
    setScheduledSlot,
    expressScheduleMode,
    setExpressScheduleMode,
    expressScheduleDates,
    expressScheduledDate,
    setExpressScheduledDate,
    expressScheduleSlots,
    expressScheduledSlot,
    setExpressScheduledSlot,
    onSubmit,
    goToAddress,
    selectStep,
    confirmation,
    closeConfirmation,
    confirmStandardDelivery,
    placeConfirmOpen,
    closePlaceConfirm,
    confirmPlaceOrder,
  } = useCheckout()
  const paymentChoices = checkoutPaymentChoices(allowedPaymentMethod)
  const paymentOptions = CHECKOUT_PAYMENT_OPTIONS.filter((option) =>
    paymentChoices.includes(option.value),
  )
  const isAddressStep = step === CHECKOUT_STEP.address
  const isCheckoutStep = step === CHECKOUT_STEP.checkout
  const placedOutcome = placedOrders?.length ? checkoutPaymentOutcome(placedOrders) : null
  const title = isAddressStep
    ? CHECKOUT_UI.addressTitle
    : placedOutcome
      ? CHECKOUT_OUTCOME_COPY[placedOutcome].title
      : CHECKOUT_UI.pageTitle
  const lead = isAddressStep
    ? CHECKOUT_UI.addressLead
    : isCheckoutStep
      ? CHECKOUT_UI.checkoutLead
      : placedOutcome
        ? CHECKOUT_OUTCOME_COPY[placedOutcome].lead
        : CHECKOUT_UI.successLead
  const submitLabel = isAddressStep
    ? CHECKOUT_UI.continueToCheckout
    : form.paymentMethod === PAYMENT_METHOD.online
      ? CHECKOUT_UI.payNow
      : CHECKOUT_UI.placeOrder
  const pendingLabel =
    form.paymentMethod === PAYMENT_METHOD.online
      ? CHECKOUT_UI.placingOnline
      : CHECKOUT_UI.placing

  if (!isAuthenticated) {
    return (
      <div className="checkout-page">
        <div className="container">
          <CheckoutHeader
            title={CHECKOUT_UI.pageTitle}
            lead={CHECKOUT_UI.emptyLoginHint}
            current={CHECKOUT_STEP.cart}
            onSelect={selectStep}
          />
          <div className="checkout-page__empty">
            <p>{CHECKOUT_UI.emptyLoginTitle}</p>
            <p className="checkout-page__empty-hint">{CHECKOUT_UI.emptyLoginHint}</p>
            <Link
              to={AUTH_PATHS.login}
              state={{ from: ORDER_PATHS.checkout }}
              className="checkout-page__continue"
            >
              {CHECKOUT_UI.loginCta}
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (placedOrders?.length) {
    return (
      <div className="checkout-page">
        <div className="container">
          <CheckoutHeader
            title={title}
            lead={lead}
            current={CHECKOUT_STEP.done}
            addressComplete
            placed
            onSelect={selectStep}
          />
          <CheckoutSuccess
            orders={placedOrders}
            split={placedOrders.length > 1}
          />
        </div>
      </div>
    )
  }

  if (lines.length === 0) {
    return (
      <div className="checkout-page">
        <div className="container">
          <CheckoutHeader
            title={CHECKOUT_UI.pageTitle}
            lead={CHECKOUT_UI.emptyHint}
            current={CHECKOUT_STEP.cart}
            onSelect={selectStep}
          />
          <div className="checkout-page__empty">
            <p>{CHECKOUT_UI.emptyTitle}</p>
            <p className="checkout-page__empty-hint">{CHECKOUT_UI.emptyHint}</p>
            <Link to="/" className="checkout-page__continue">
              {CHECKOUT_UI.continueShopping}
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="checkout-page checkout-page--dock">
      <div className="container">
        <CheckoutHeader
          title={title}
          lead={lead}
          current={step}
          addressComplete={addressComplete}
          onSelect={selectStep}
        />

        <form onSubmit={onSubmit}>
          <div className="checkout-page__layout">
          <div className="checkout-main">
            {isAddressStep ? (
              <>
                <section className="checkout-card">
                  <header className="checkout-card__head">
                    <h2>{CHECKOUT_UI.deliveryHeading}</h2>
                    <p>{CHECKOUT_UI.deliveryLead}</p>
                  </header>
                  <div className="checkout-form">
                    <label className="checkout-field">
                      <span>{CHECKOUT_UI.recipientName}</span>
                      <input
                        value={form.recipientName}
                        onChange={(event) =>
                          updateForm({ recipientName: event.target.value })
                        }
                        required
                        maxLength={160}
                        disabled={submitting}
                      />
                    </label>
                    <label className="checkout-field">
                      <span>{CHECKOUT_UI.recipientPhone}</span>
                      <input
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel-national"
                        value={form.recipientPhone}
                        onChange={(event) =>
                          updateForm({
                            recipientPhone: event.target.value.replace(/\D/g, '').slice(0, 10),
                          })
                        }
                        required
                        minLength={10}
                        maxLength={10}
                        disabled={submitting}
                      />
                    </label>
                    <label className="checkout-field checkout-field--wide">
                      <span>{CHECKOUT_UI.deliveryArea}</span>
                      <textarea
                        value={locationLabel || ''}
                        readOnly
                        rows={2}
                        disabled={submitting}
                      />
                      <em className="checkout-field__hint">{ADDRESS_UI.areaLocked}</em>
                    </label>
                    <label className="checkout-field checkout-field--wide">
                      <span>{CHECKOUT_UI.address}</span>
                      <textarea
                        value={form.address}
                        onChange={(event) => updateForm({ address: event.target.value })}
                        required
                        maxLength={500}
                        rows={2}
                        disabled={submitting}
                      />
                    </label>
                    <label className="checkout-field">
                      <span>{CHECKOUT_UI.city}</span>
                      <input value={city || ''} readOnly disabled={submitting} />
                    </label>
                    <label className="checkout-field">
                      <span>{CHECKOUT_UI.pincode}</span>
                      {pincodeEditable ? (
                        <>
                          <input
                            inputMode="numeric"
                            autoComplete="postal-code"
                            value={form.pincode}
                            onChange={(event) =>
                              updateForm({ pincode: sanitizePincodeInput(event.target.value) })
                            }
                            placeholder={CHECKOUT_UI.pincodePlaceholder}
                            required
                            minLength={PINCODE_LENGTH}
                            maxLength={PINCODE_LENGTH}
                            disabled={submitting}
                          />
                          <em className="checkout-field__hint">
                            {CHECKOUT_UI.pincodeManualHint}
                          </em>
                        </>
                      ) : (
                        <input
                          inputMode="numeric"
                          value={pincodeResolving ? CHECKOUT_UI.pincodeResolving : shippingPincode}
                          readOnly
                          required
                          disabled={submitting}
                        />
                      )}
                    </label>
                    <label className="checkout-field checkout-field--wide">
                      <span>{CHECKOUT_UI.notes}</span>
                      <textarea
                        value={form.notes}
                        onChange={(event) => updateForm({ notes: event.target.value })}
                        placeholder={CHECKOUT_UI.notesPlaceholder}
                        maxLength={500}
                        rows={2}
                        disabled={submitting}
                      />
                    </label>
                  </div>
                  <SavedAddressList
                    addresses={savedAddresses}
                    selectedId={selectedAddressId}
                    disabled={submitting}
                    onSelect={selectSavedAddress}
                  />
                </section>

                <section className="checkout-card">
                  <header className="checkout-card__head">
                    <h2>{CHECKOUT_UI.billingHeading}</h2>
                    <p>{CHECKOUT_UI.billingLead}</p>
                  </header>
                  <label className="checkout-check">
                    <input
                      type="checkbox"
                      checked={form.billingSameAsShipping}
                      onChange={(event) => setBillingSameAsShipping(event.target.checked)}
                      disabled={submitting}
                    />
                    <span>{CHECKOUT_UI.billingSameAsShipping}</span>
                  </label>
                  {form.billingSameAsShipping ? null : (
                    <div className="checkout-form">
                      <label className="checkout-field">
                        <span>{CHECKOUT_UI.billingName}</span>
                        <input
                          value={form.billingName}
                          onChange={(event) => updateForm({ billingName: event.target.value })}
                          required
                          maxLength={160}
                          disabled={submitting}
                        />
                      </label>
                      <label className="checkout-field">
                        <span>{CHECKOUT_UI.billingPhone}</span>
                        <input
                          type="tel"
                          inputMode="numeric"
                          autoComplete="tel-national"
                          value={form.billingPhone}
                          onChange={(event) =>
                            updateForm({
                              billingPhone: event.target.value.replace(/\D/g, '').slice(0, 10),
                            })
                          }
                          required
                          minLength={10}
                          maxLength={10}
                          disabled={submitting}
                        />
                      </label>
                      <label className="checkout-field checkout-field--wide">
                        <span>{CHECKOUT_UI.billingAddress}</span>
                        <textarea
                          value={form.billingAddress}
                          onChange={(event) =>
                            updateForm({ billingAddress: event.target.value })
                          }
                          required
                          maxLength={500}
                          rows={3}
                          disabled={submitting}
                        />
                      </label>
                      <label className="checkout-field">
                        <span>{CHECKOUT_UI.city}</span>
                        <input
                          value={form.billingCity}
                          onChange={(event) => updateForm({ billingCity: event.target.value })}
                          required
                          maxLength={120}
                          disabled={submitting}
                        />
                      </label>
                      <label className="checkout-field">
                        <span>{CHECKOUT_UI.pincode}</span>
                        <input
                          inputMode="numeric"
                          value={form.billingPincode}
                          onChange={(event) =>
                            updateForm({
                              billingPincode: event.target.value.replace(/\D/g, '').slice(0, 12),
                            })
                          }
                          required
                          maxLength={12}
                          disabled={submitting}
                        />
                      </label>
                    </div>
                  )}
                </section>
              </>
            ) : (
              <>
                <section className="checkout-card checkout-recap">
                  <header className="checkout-recap__head">
                    <div>
                      <p className="checkout-recap__label">{CHECKOUT_UI.deliverTo}</p>
                      <p className="checkout-recap__value">{addressRecap}</p>
                    </div>
                    <button
                      type="button"
                      className="checkout-recap__change"
                      onClick={goToAddress}
                    >
                      {CHECKOUT_UI.changeAddress}
                    </button>
                  </header>
                </section>

                <CheckoutDeliveryPlan
                  estimate={deliveryEstimate}
                  canSplit={canSplit}
                  canSchedule={canSchedule}
                  canScheduleExpress={canScheduleExpress}
                  expressItems={expressItems}
                  standardItems={standardItems}
                  splitMode={splitMode}
                  onSplitModeChange={setSplitMode}
                  scheduleMode={scheduleMode}
                  onScheduleModeChange={setScheduleMode}
                  dates={scheduleDates}
                  selectedDate={scheduledDate}
                  onDateChange={setScheduledDate}
                  slots={scheduleSlots}
                  selectedSlot={scheduledSlot}
                  onSlotChange={setScheduledSlot}
                  expressScheduleMode={expressScheduleMode}
                  onExpressScheduleModeChange={setExpressScheduleMode}
                  expressDates={expressScheduleDates}
                  expressSelectedDate={expressScheduledDate}
                  onExpressDateChange={setExpressScheduledDate}
                  expressSlots={expressScheduleSlots}
                  expressSelectedSlot={expressScheduledSlot}
                  onExpressSlotChange={setExpressScheduledSlot}
                  disabled={submitting}
                />

                {paymentOptions.length === 1 ? (
                  <section className="checkout-card checkout-payment checkout-payment--compact">
                    <p className="checkout-payment__compact">
                      <span>{CHECKOUT_UI.paymentCompactLead}</span>
                      <strong>{paymentOptions[0].label}</strong>
                      <em>{paymentOptions[0].hint}</em>
                    </p>
                  </section>
                ) : (
                  <fieldset className="checkout-card checkout-payment">
                    <legend className="checkout-card__head">
                      <h2>{CHECKOUT_UI.paymentHeading}</h2>
                    </legend>
                    <div className="checkout-payment__options">
                      {paymentOptions.map((option) => (
                        <label
                          key={option.value}
                          className={cx(
                            'checkout-payment__option',
                            form.paymentMethod === option.value && 'is-selected',
                          )}
                        >
                          <input
                            type="radio"
                            name="paymentMethod"
                            value={option.value}
                            checked={form.paymentMethod === option.value}
                            onChange={() =>
                              updateForm({ paymentMethod: option.value })
                            }
                            disabled={submitting}
                          />
                          <span>
                            <strong>{option.label}</strong>
                            <em>{option.hint}</em>
                          </span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                )}
              </>
            )}

            {error ? (
              <p className="checkout-page__error" role="alert">
                {error}
              </p>
            ) : null}
          </div>

          <div className="checkout-bill">
          <aside
            className={cx('checkout-summary', savings > 0 && 'checkout-summary--saved')}
          >
            {savings > 0 ? (
              <p className="checkout-summary__saved">
                {CART_UI.savedBanner} {formatCurrency(savings)}
              </p>
            ) : null}
            <header className="checkout-summary__head">
              <h2>{CART_UI.billTitle}</h2>
              <span>
                {itemCount} {itemCount === 1 ? CART_UI.itemSingular : CART_UI.itemPlural}
              </span>
            </header>
            <ul className="checkout-summary__lines">
              {lines.map((line) => {
                const extraAddons = addonsForCheckoutLine(lineAddons, line.id)
                const meta = checkoutLineMeta(line)
                const wasTotal = cartLineWasTotal(line)
                return (
                  <li key={line.id} className="checkout-line">
                          <Link
                            to={`/product/${line.productSlug}`}
                            className="checkout-line__media"
                          >
                            {line.imageUrl ? (
                              <img src={line.imageUrl} alt="" />
                            ) : (
                              <span className="checkout-line__placeholder" aria-hidden />
                            )}
                          </Link>
                          <div className="checkout-line__body">
                            <Link
                              to={`/product/${line.productSlug}`}
                              className="checkout-line__name"
                            >
                              {line.name}
                            </Link>
                            <p className="checkout-line__qty">
                              × {line.quantity}
                              {meta.length > 0 ? ` · ${meta.join(' · ')}` : ''}
                            </p>
                            {isCheckoutStep && extraAddons.length > 0 ? (
                              <div className="checkout-line__addons">
                                <p>{CHECKOUT_UI.addonsHeading}</p>
                                <div className="checkout-line__addon-list">
                                  {extraAddons.map((addon) => (
                                    <button
                                      key={addon.id}
                                      type="button"
                                      className="checkout-addon"
                                      disabled={submitting}
                                      onClick={() => addLineAddon(line.id, addon)}
                                    >
                                      {addon.imageUrl ? (
                                        <img src={addon.imageUrl} alt="" />
                                      ) : (
                                        <span aria-hidden>
                                          <Icon name={ICON_FALLBACK.addon} size={14} />
                                        </span>
                                      )}
                                      <em>{addon.label}</em>
                                      <strong>{formatAddonPrice(addon.price)}</strong>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ) : null}
                          </div>
                          <p className="checkout-line__price cart-price">
                            {wasTotal != null ? (
                              <span className="cart-price__was">{formatCurrency(wasTotal)}</span>
                            ) : null}
                            <strong>{formatCurrency(line.unitPrice * line.quantity)}</strong>
                          </p>
                  </li>
                )
              })}
            </ul>
            {isCheckoutStep ? (
              <div className="checkout-coupon">
                <label className="checkout-coupon__label" htmlFor="checkout-coupon">
                  {CHECKOUT_UI.couponLabel}
                </label>
                <div className="checkout-coupon__row">
                  <input
                    id="checkout-coupon"
                    value={couponInput}
                    onChange={(event) => setCouponInput(event.target.value)}
                    placeholder={CHECKOUT_UI.couponPlaceholder}
                    autoComplete="off"
                    disabled={submitting}
                  />
                  <button
                    type="button"
                    className="checkout-coupon__apply"
                    onClick={applyCoupon}
                    disabled={submitting}
                  >
                    {CHECKOUT_UI.couponApply}
                  </button>
                </div>
                {couponError ? (
                  <p className="checkout-coupon__error" role="alert">
                    {couponError}
                  </p>
                ) : null}
              </div>
            ) : null}
            <div className="checkout-summary__row">
              <span>{CART_UI.orderTotalLabel}</span>
              <p className="cart-price">
                {compareAtTotal != null ? (
                  <span className="cart-price__was">{formatCurrency(compareAtTotal)}</span>
                ) : null}
                <strong>{formatCurrency(subtotal)}</strong>
              </p>
            </div>
            <div className="checkout-summary__row checkout-summary__row--total">
              <span>{CART_UI.grandTotalLabel}</span>
              <strong>{formatCurrency(subtotal)}</strong>
            </div>
            <button type="submit" className="checkout-page__submit" disabled={submitting}>
              {submitting ? <ButtonSpinner label={pendingLabel} /> : submitLabel}
            </button>
          </aside>
          {isAddressStep ? <p className="checkout-bill__note">{CART_UI.couponNote}</p> : null}
          </div>
          </div>
          <div className="checkout-dock">
            <div className="checkout-dock__total">
              <span>{CART_UI.grandTotalLabel}</span>
              <p className="cart-price">
                {compareAtTotal != null ? (
                  <span className="cart-price__was">{formatCurrency(compareAtTotal)}</span>
                ) : null}
                <strong>{formatCurrency(subtotal)}</strong>
              </p>
              {savings > 0 ? (
                <em className="checkout-dock__save">
                  {CART_UI.savedLabel} {formatCurrency(savings)}
                </em>
              ) : null}
            </div>
            <button type="submit" className="checkout-dock__action" disabled={submitting}>
              {submitting ? <ButtonSpinner label={pendingLabel} /> : submitLabel}
            </button>
          </div>
        </form>
      </div>
      {placeConfirmOpen ? (
        <CheckoutDialog
          title={CHECKOUT_UI.confirmTitle}
          lead={CHECKOUT_UI.confirmLead}
          cancelLabel={CHECKOUT_UI.confirmCancel}
          confirmLabel={CHECKOUT_UI.confirmPlace}
          labelledBy="checkout-place-title"
          describedBy="checkout-place-lead"
          dialogLabel={CHECKOUT_UI.confirmDialogLabel}
          closeAria={CHECKOUT_UI.closeAria}
          marked
          onCancel={closePlaceConfirm}
          onConfirm={() => {
            void confirmPlaceOrder()
          }}
        >
          <p className="checkout-dialog__total">
            <span>{CART_UI.grandTotalLabel}</span>
            <span className="cart-price">
              {compareAtTotal != null ? (
                <span className="cart-price__was">{formatCurrency(compareAtTotal)}</span>
              ) : null}
              <strong>{formatCurrency(subtotal)}</strong>
            </span>
          </p>
        </CheckoutDialog>
      ) : null}
      {submitting && placingPhase ? (
        <CheckoutPlacing
          phase={placingPhase}
          online={form.paymentMethod === PAYMENT_METHOD.online}
        />
      ) : null}
      {confirmation ? (
        <ExpressConfirmDialog
          confirmation={confirmation}
          submitting={submitting}
          onCancel={closeConfirmation}
          onConfirm={() => {
            void confirmStandardDelivery()
          }}
        />
      ) : null}
    </div>
  )
}

function CheckoutHeader({
  title,
  lead,
  current,
  addressComplete = false,
  placed = false,
  onSelect,
}: CheckoutHeaderProps) {
  return (
    <header className="checkout-page__head">
      <div>
        <h1>{title}</h1>
        <p className="checkout-page__lead">{lead}</p>
      </div>
      <CheckoutStepper
        current={current}
        addressComplete={addressComplete}
        placed={placed}
        onSelect={onSelect}
      />
    </header>
  )
}
